"use client";

import { CheckIcon } from "lucide-react";
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from "../atoms/item";
import { cn } from "../lib";
import { matchesPersonSearch } from "../lib/user-search";
import { SearchList } from "./search-list";
import { UserAvatar } from "./user-avatar";

export type UserSearchListUser = {
  id: string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
  /**
   * An explicit short handle for this person where the caller has one — a
   * round member's initials, say. Searchable, but not displayed: the row
   * already shows the avatar's initials.
   */
  initials?: string | null;
  disabled?: boolean;
};

/**
 * One person, as a row in a picker: avatar, name, email.
 *
 * Exported because the people picker comes in two shapes — pick one person
 * (`UserSearchList`, the impersonation menu) and pick several (a
 * `MultiSelectField`, the feature-flag targeting row) — and both must show a
 * person identically. The shells differ; the row does not.
 */
export function UserSearchRow({
  user,
  selected = false,
  ...rest
}: React.ComponentProps<typeof Item> & {
  user: UserSearchListUser;
  /** Ticks the row when it is the current choice of a pick-one list. */
  selected?: boolean;
}) {
  return (
    // The command palette supplies its own tighter padding, which wins the
    // class merge, so the row height is set here rather than inherited: a
    // min-height cannot be undone by whichever padding happens to win.
    <Item
      size="sm"
      {...rest}
      className={cn("pointer-coarse:min-h-11", rest.className)}
    >
      <ItemMedia>
        <UserAvatar className="size-8" user={user} />
      </ItemMedia>
      <ItemContent>
        <ItemTitle>{user.name}</ItemTitle>
        <ItemDescription>{user.email}</ItemDescription>
      </ItemContent>
      {selected && (
        <>
          <CheckIcon className="text-accent size-4 shrink-0" />
          <span className="sr-only">(selected)</span>
        </>
      )}
    </Item>
  );
}

/**
 * A searchable list of people to pick from: a search box over a roster the
 * caller supplies and orders.
 *
 * A thin people-flavoured `SearchList` — it supplies the person matcher (see
 * ../lib/user-search for what counts as a match) and the person row, and
 * inherits everything else, including the rule that searching preserves the
 * caller's ordering rather than reranking by relevance.
 */
export function UserSearchList({
  users,
  onSelect,
  placeholder = "Search people...",
  emptyMessage = "No user found.",
  autoFocus = false,
  disabled = false,
  className,
}: {
  users: UserSearchListUser[];
  onSelect: (userId: string) => void;
  placeholder?: string;
  emptyMessage?: string;
  autoFocus?: boolean;
  /** Disables every row — for while a selection is being applied. */
  disabled?: boolean;
  className?: string;
}) {
  return (
    <SearchList
      items={users}
      match={matchesPersonSearch}
      renderItem={(user) => <UserSearchRow user={user} />}
      onSelect={onSelect}
      placeholder={placeholder}
      emptyMessage={emptyMessage}
      autoFocus={autoFocus}
      disabled={disabled}
      className={className}
    />
  );
}
